import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { BillingService, type LedgerEntry } from '../../services/billingService';
import { queryKeys } from '../../queries/keys';
import { SignedOutPanel } from '../../components/auth/SignedOutPanel';
import { useAuthStore } from '../../store/authStore';
import { useCredits } from '../../credits/useCredits';
import { formatNgn } from '../../credits/insufficientCredits';

/**
 * How each ledger kind reads to someone who did not write the billing system.
 * "Hold" and "Settle" are our words, not theirs — a user sees a charge and a
 * correction, which is what actually happened to their money.
 */
const KIND_LABEL: Record<string, string> = {
  purchase: 'Top-up',
  grant: 'Welcome credit',
  hold: 'Reserved',
  settle: 'Adjustment',
  release: 'Refunded',
  refund: 'Refunded',
  adjustment: 'Adjustment',
};

/** What the entry was for, when we know. */
function describe(entry: LedgerEntry): string {
  if (entry.kind === 'purchase') return 'Card payment';
  if (entry.kind === 'grant') return 'Added when you signed up';
  if (entry.model) {
    const lab = entry.lab ? `${entry.lab} · ` : '';
    return `${lab}${entry.model}`;
  }
  return KIND_LABEL[entry.kind] ?? entry.kind;
}

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Credits: what you have, how to add more, and where it went.
 *
 * Top-ups are deliberately not implemented here — the button opens the same
 * drawer used everywhere else, so there is one payment flow to reason about and
 * one place it can break.
 */
export const BillingPage: React.FC = () => {
  const isSignedIn = useAuthStore(state => state.isAuthenticated);
  const { balance, enforced, openTopUp } = useCredits();

  /** Set when arriving here recovered a payment the webhook never delivered. */
  const [rescued, setRescued] = useState<number | null>(null);

  const {
    data: ledger,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.creditLedger(1),
    queryFn: () => BillingService.getLedger(1, 50),
    enabled: isSignedIn,
  });

  // This page is the landing target after hosted checkout, so it is exactly where a user
  // arrives expecting to see their money. Reconcile on arrival rather than trusting that the
  // provider's webhook got through — it is idempotent, and credits nothing when all is well.
  const qc = useQueryClient();
  useEffect(() => {
    if (!isSignedIn) return;
    let cancelled = false;
    BillingService.sync()
      .then(result => {
        if (cancelled || result.credited === 0) return;
        setRescued(result.creditedNgn);
        void qc.invalidateQueries({ queryKey: queryKeys.creditBalance() });
        void qc.invalidateQueries({ queryKey: queryKeys.creditLedger(1) });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, qc]);

  const { data: packs } = useQuery({
    queryKey: queryKeys.creditPacks(),
    queryFn: () => BillingService.getPacks(),
    enabled: isSignedIn,
    staleTime: 5 * 60_000,
  });

  if (!isSignedIn) {
    return (
      <Box sx={{ height: '100vh', bgcolor: 'background.default' }}>
        <SignedOutPanel
          what="your credit"
          detail="Sign in to see your balance, top up, and review what you have spent."
        />
      </Box>
    );
  }

  const canBuy = packs?.purchaseAvailable ?? false;

  return (
    <Box sx={{ height: '100vh', overflowY: 'auto', bgcolor: 'background.default', pb: 8 }}>
      <Container maxWidth="md" sx={{ pt: 4 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <AccountBalanceWalletIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Credit and Billing
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
          Credit pays for work run on our provider accounts. Add your own key for a provider in
          API keys and that provider&apos;s work stops costing credit entirely.
        </Typography>

        {rescued !== null && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setRescued(null)}>
            We found a payment of {formatNgn(rescued)} that hadn&apos;t been applied yet and
            added it to your balance.
          </Alert>
        )}

        {!enforced && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Billing is in preview here: prices are shown as estimates and nothing is charged yet.
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Balance
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {balance ? formatNgn(balance.balanceNgn) : <Skeleton width={140} />}
              </Typography>
            </Box>
            {canBuy && (
              <Button variant="contained" size="large" onClick={openTopUp}>
                Add credit
              </Button>
            )}
          </Stack>
          {!canBuy && packs && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Card payments aren&apos;t available here yet. Add your own provider keys to keep
              working without credit.
            </Alert>
          )}
        </Paper>

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          History
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Couldn&apos;t load your history. Reload to try again.
          </Alert>
        )}

        <Paper variant="outlined">
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <Box key={i} sx={{ p: 2 }}>
                <Skeleton height={28} />
              </Box>
            ))}

          {!isLoading && ledger?.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Nothing here yet. Your top-ups and usage will appear as you work.
              </Typography>
            </Box>
          )}

          {ledger?.map((entry, i) => {
            const positive = entry.amountNgn > 0;
            return (
              <Box key={entry.id}>
                {i > 0 && <Divider />}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 2 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {KIND_LABEL[entry.kind] ?? entry.kind}
                      </Typography>
                      {entry.kind === 'grant' && (
                        <Chip size="small" label="Free" color="success" variant="outlined" />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {describe(entry)} · {when(entry.createdAt)}
                    </Typography>
                  </Box>
                  <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={positive ? 'success.main' : 'text.primary'}
                    >
                      {positive ? '+' : '−'}
                      {formatNgn(Math.abs(entry.amountNgn))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatNgn(entry.balanceAfterNgn)}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Paper>
      </Container>
    </Box>
  );
};
